import createClient from "openapi-fetch";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];

// Same origin in production (FastAPI serves the SPA) and in dev (Vite proxies /api),
// so the session cookie rides along without any CORS setup.
export const api = createClient<paths>({ baseUrl: "", credentials: "same-origin" });

/** The backend's error envelope, thrown so callers can branch on errorCode. */
export class ApiError extends Error {
  readonly status: number;
  readonly errorCode: string;
  readonly data: Record<string, unknown>;

  constructor(status: number, errorCode: string, message: string, data: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.errorCode = errorCode;
    this.data = data;
  }
}

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

function toApiError(response: Response, error: unknown): ApiError {
  if (error && typeof error === "object" && "error_code" in error) {
    const e = error as { error_code: string; message?: string; data?: Record<string, unknown> };
    return new ApiError(response.status, e.error_code, e.message ?? e.error_code, e.data ?? {});
  }
  return new ApiError(response.status, `http_${response.status}`, response.statusText || "Request failed");
}

/** Resolve an openapi-fetch call to its data, or throw ApiError. */
export async function unwrap<T>(call: Promise<FetchResult<T>>): Promise<T> {
  let result: FetchResult<T>;
  try {
    result = await call;
  } catch {
    throw new ApiError(0, "network_error", "Cannot reach the server.");
  }
  if (!result.response.ok) {
    throw toApiError(result.response, result.error);
  }
  return result.data as T;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.errorCode === "validation_error") {
      const errors = (error.data.errors as { loc: unknown[]; msg: string }[] | undefined) ?? [];
      if (errors.length) {
        return errors.map((e) => `${e.loc.slice(1).join(".")}: ${e.msg}`).join("; ");
      }
    }
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}
